import { useState, useEffect } from "react";
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Avatar,
  Typography,
  Button,
  Box,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link } from "react-router-dom";
import Iconify from "../../../components/iconify";
import PhotoSelector from "./PhotoSelector";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PasswordStrengthBar = ({ strength }) => {
  const getStrengthProps = () => {
    switch (strength) {
      case "weak":
        return { color: "error", value: 33, label: "Weak" };
      case "medium":
        return { color: "warning", value: 66, label: "Medium" };
      case "strong":
        return { color: "success", value: 100, label: "Strong" };
      default:
        return { value: 0 };
    }
  };
  const props = getStrengthProps();
  if (props.value === 0) return null;

  return (
    <Box>
      <LinearProgress
        variant="determinate"
        sx={{ height: 6, borderRadius: 1 }}
        {...props}
      />
      <Typography
        variant="caption"
        color={`${props.color}.main`}
        sx={{ display: "block", textAlign: "right", mt: 0.5 }}
      >
        {props.label}
      </Typography>
    </Box>
  );
};

const Register3 = ({ onNext, onPrevious, data, isRegistering }) => {
  const [formData, setFormData] = useState(data);
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    setFormData(data);
    if (data.email) validateEmail(data.email);
    if (data.password) validatePassword(data.password);
  }, [data]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (name === "password") validatePassword(value);
    if (name === "email") validateEmail(value);
  };

  const handlePhotoSelect = (photoDataUrl) => {
    setFormData((prevData) => ({
      ...prevData,
      profilePicture: photoDataUrl,
    }));
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    onPrevious(formData);
  };

  const handleNext = (e) => {
    e.preventDefault();

    validateEmail(formData.email);
    validatePassword(formData.password);

    if (!emailError && !passwordError && acceptedTerms) {
      onNext({ ...formData, acceptedTermsOfUse: acceptedTerms });
    }
  };

  const handleRemovePhoto = () => {
    setFormData((prevData) => ({ ...prevData, profilePicture: null }));
  };

  const validateEmail = (email) => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    setEmailError(isValidEmail ? "" : "Please enter a valid email address.");
  };

  const validatePassword = (password) => {
    let strengthIndicator = -1;
    let upper = false,
      lower = false,
      numbers = false,
      firstname = false,
      lastname = false,
      username = false,
      specialChars = false;

    if (/[A-Z]/.test(password)) upper = true;
    if (/[a-z]/.test(password)) lower = true;
    if (/\d/.test(password)) numbers = true;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password))
      specialChars = true;
    if (
      formData.password
        .toLowerCase()
        .trim()
        .includes(formData.firstName.toLowerCase().trim())
    )
      firstname = true;
    if (
      formData.password
        .toLowerCase()
        .trim()
        .includes(formData.lastName.toLowerCase().trim())
    )
      lastname = true;
    if (
      formData.password
        .toLowerCase()
        .trim()
        .includes(formData.userName.toLowerCase().trim())
    )
      username = true;

    if (password.length >= 8) {
      strengthIndicator++;
      if (!firstname && !lastname && !username && password.length > 5)
        strengthIndicator++;
    }
    if (upper && lower && numbers && specialChars) strengthIndicator++;

    setStrength(["weak", "medium", "strong"][strengthIndicator] || "");

    switch (true) {
      case password.length < 8:
        setPasswordError("Password must be at least 8 characters long.");
        break;
      case firstname || lastname:
        setPasswordError("Password cannot contain your first or last name.");
        break;
      case username:
        setPasswordError("Password cannot contain your username.");
        break;
      case !specialChars:
        setPasswordError(
          "Password must contain at least one special character."
        );
        break;
      case !upper:
        setPasswordError(
          "Password must contain at least one uppercase character."
        );
        break;
      case !lower:
        setPasswordError(
          "Password must contain at least one lowercase character."
        );
        break;
      case !numbers:
        setPasswordError("Password must contain at least one number.");
        break;
      default:
        setPasswordError("");
        break;
    }
  };

  return (
    <>
      <Stack spacing={1.5}>
        <TextField
          name="email"
          label="Email Address"
          value={formData.email}
          onChange={handleChange}
          error={!!emailError}
          helperText={emailError}
        />

        <TextField
          name="password"
          label="Password"
          value={formData.password}
          type={showPassword ? "text" : "password"}
          onChange={handleChange}
          error={!!passwordError}
          helperText={passwordError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <PasswordStrengthBar strength={strength} />

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Profile Picture
          </Typography>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Avatar
              src={formData.profilePicture}
              sx={{ width: 64, height: 64 }}
            />
            <Stack spacing={0.5} alignItems="flex-start">
              <PhotoSelector
                onSelect={handlePhotoSelect}
                hasPhoto={!!formData.profilePicture}
              />
              {formData.profilePicture && (
                <Button size="small" color="error" onClick={handleRemovePhoto}>
                  Remove
                </Button>
              )}
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 1 }}
          >
            Allowed formats: JPG, PNG. Maximum file size: 1 MB.
          </Typography>
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I have read and accept the{" "}
                <MuiLink
                  component={Link}
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  Terms of Use
                </MuiLink>
                {" *"}
              </Typography>
            }
          />
        </Box>
      </Stack>

      <Box sx={{ my: 3 }}>
        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            size="large"
            variant="outlined"
            onClick={handlePrevious}
            disabled={isRegistering}
          >
            Previous
          </Button>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            loading={isRegistering}
            onClick={handleNext}
            disabled={
              isRegistering ||
              !!emailError ||
              !!passwordError ||
              strength.trim() !== "strong" ||
              !acceptedTerms
            }
          >
            Create Account
          </LoadingButton>
        </Stack>
      </Box>
      <ToastContainer />
    </>
  );
};

export default Register3;
